/**
 * Firebase の遅延初期化 (§4.3)。SDK は初回利用時に動的 import (+100-130KB gzip を
 * 初回描画から外す)。firestore/lite は onSnapshot 欠如のため不可 (§決定要約)。
 *
 * VITE_FIREBASE_PROJECT_ID 未設定 = 「Firebase 無効モード」— 呼び出し側は
 * isFirebaseConfigured() で分岐し、現行 localStorage 動作を維持する。
 */
import type { FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';

export interface FirebaseHandles {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
}

let handles: Promise<FirebaseHandles> | null = null;

export function isFirebaseConfigured(): boolean {
  // 開発時のログイン不要モード: VITE_DEV_SKIP_AUTH=1 のとき Firebase を「無効」扱いに
  // する。これで認証ストアが即 ready (email='local@dev') になり、データは
  // localStorage に載る (Firestore ルールは認証を要求するため、認証だけ飛ばすと
  // トークン無しで書き込みが失敗する半壊状態になる — 両方まとめて無効化するのが正)。
  //
  // 本番ビルド (vite build) では import.meta.env.DEV が false になるため、フラグを
  // 誤って残しても production は必ず実認証を通る。dev サーバ (npm run dev) 限定。
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_SKIP_AUTH === '1') {
    return false;
  }
  return Boolean(import.meta.env.VITE_FIREBASE_PROJECT_ID);
}

export function getFirebase(): Promise<FirebaseHandles> {
  if (handles) return handles;
  handles = (async () => {
    const [{ initializeApp }, authMod, fsMod] = await Promise.all([
      import('firebase/app'),
      import('firebase/auth'),
      import('firebase/firestore'),
    ]);
    const app = initializeApp({
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    });
    const auth = authMod.getAuth(app);
    // 既定 = メモリキャッシュ。IndexedDB persistence は有効化しない (§決定要約)。
    const db = fsMod.getFirestore(app);
    if (import.meta.env.VITE_FIREBASE_EMULATOR === '1') {
      authMod.connectAuthEmulator(auth, 'http://127.0.0.1:9099', {
        disableWarnings: true,
      });
      fsMod.connectFirestoreEmulator(db, '127.0.0.1', 8089);
    }
    return { app, auth, db };
  })();
  // 初期化失敗 (ネットワーク断等の一時的要因) で handles を Promise<rejected> のまま
  // 固定してしまうと、以後の全呼び出しが同じ reject 済み Promise を返し続け
  // (poisoned singleton)、リロードなしでは復旧できなくなる。失敗時は null に
  // 戻し、次回呼び出しでの再初期化 (リトライ) を可能にする。
  handles.catch(() => {
    handles = null;
  });
  return handles;
}
